import { AsyncPipe } from '@angular/common';
import { AfterViewInit, Component, inject, Input, input, OnChanges, OnDestroy, output } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldAppearance, MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';

import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { GraphGroup, GraphProfile, UnsubscribeService } from '@webgate/angular-azure-core';
import { debounceTime, Subscription } from 'rxjs';
import { UserNamePipe } from 'src/app/pipes/user-name.pipe';
import { AzureIdStatusPipe } from 'src/app/pipes/azure-id-status.pipe';
import { CompareService } from 'src/app/services/compare.service';
import { UserService } from 'src/app/services/user.service';

@Component({
  selector: 'app-user-search',
  templateUrl: './user-search.component.html',
  styleUrls: ['./user-search.component.scss'],
  imports: [
    MatDialogModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    CKEditorModule,
    MatIconModule,
    MatButtonModule,
    MatLabel,
    MatTableModule,
    MatSortModule,
    MatChipsModule,
    MatPaginatorModule,
    UserNamePipe,
    AzureIdStatusPipe,
    AsyncPipe,
    MatCheckboxModule
  ]
})
export class UserSearchComponent implements OnChanges, AfterViewInit, OnDestroy {
  readonly hideChips = input(false);
  readonly singleSelect = input(false);
  @Input() selectedRows: GraphProfile[] = [];
  @Input() selectedRowsGroups: GraphGroup[] = [];
  readonly appearance = input<MatFormFieldAppearance>('outline');

  readonly filterValueEmitter = output<{
    users: GraphProfile[];
    groups?: GraphGroup[];
  }>();

  userService = inject(UserService);
  unsubscribeService = inject(UnsubscribeService);
  compareService = inject(CompareService);
  private searchSubscription?: Subscription;
  private loadSubscription?: Subscription;

  filterInputControl: FormControl = new FormControl();
  columnDef: string[] = ['checkbox', 'firstName', 'lastName', 'mail'];

  dataSource = new MatTableDataSource<GraphProfile>();

  loaded = false;

  ngOnChanges(): void {
    this.filterInputControl.markAsTouched();
    this.filterInputControl.updateValueAndValidity();
  }

  ngAfterViewInit(): void {
    this.loadSubscription = this.userService.loadAllUser().subscribe(() => {
      this.handleSearch();
      this.loaded = true;
    });
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    if (this.loadSubscription) {
      this.loadSubscription.unsubscribe();
    }
  }

  handleSearch(): void {
    this.searchSubscription = this.filterInputControl.valueChanges.pipe(debounceTime(1000)).subscribe((searchTerm) => {
      if (searchTerm) {
        this.search(searchTerm);
      }
    });
  }

  search(searchTerm: string): void {
    this.dataSource.data = this.userService.getByNameOrEmail(searchTerm);
  }

  onRowClick(rowEvent: GraphProfile): void {
    if (this.checkIfExistsInSelectedRows(rowEvent)) {
      this.removeFromSelectedRows(rowEvent);
    } else {
      this.addToSelectedRows(rowEvent);
    }
    this.emitCoworkerSelected(this.selectedRows);
    this.filterInputControl.updateValueAndValidity();
  }

  checkIfExistsInSelectedRows(rowEvent: GraphProfile): boolean {
    return this.selectedRows.map((r) => r.id).includes(rowEvent.id);
  }

  private addToSelectedRows(rowEvent: GraphProfile): void {
    if (this.singleSelect() && this.selectedRows.length) this.selectedRows = [rowEvent];
    else this.selectedRows.push(rowEvent);
    this.filterInputControl.reset();
  }

  private removeFromSelectedRows(rowEvent: GraphProfile): void {
    this.selectedRows.splice(
      this.selectedRows.findIndex((row) => row.id === rowEvent.id),
      1
    );
  }

  private emitCoworkerSelected(emitValue: GraphProfile[]): void {
    this.filterValueEmitter.emit({
      users: emitValue,
      groups: this.selectedRowsGroups
    });
  }

  removeAll(): void {
    this.selectedRows = [];
    if (this.selectedRowsGroups.length) this.selectedRowsGroups = [];
    this.emitCoworkerSelected(this.selectedRows);
  }

  removeFromGroup(rowEvent: GraphGroup): void {
    this.selectedRowsGroups.splice(
      this.selectedRowsGroups.findIndex((row) => row.id === rowEvent.id),
      1
    );
  }

  sortData(sort: Sort): void {
    this.dataSource.data = this.dataSource.data.sort((a: GraphProfile, b: GraphProfile) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'firstName':
          return this.compareService.compare(a.displayName?.split(' ')[0], b.displayName?.split(' ')[0], isAsc);
        case 'lastName':
          return this.compareService.compare(a.displayName?.split(' ')[1], b.displayName?.split(' ')[1], isAsc);
        case 'mail':
          return this.compareService.compare(a.mail, b.mail, isAsc);
        default:
          return this.compareService.compare(a.displayName, b.displayName, isAsc);
      }
    });
  }
}
